import { refreshOrderTasks } from '@/lib/order-task-refresh'
import { useOrderDetailModal } from '@/stores/order-detail-modal'
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { SSEClient, type SSEMessage, type NotificationMessage } from '@/lib/sse-client'
import { toast } from '@/lib/sonner'
import { useQueryClient } from '@tanstack/react-query'

interface NotificationContextType {
  isConnected: boolean
  unreadCount: number
  refreshUnreadCount: () => void
}

const NotificationContext = createContext<NotificationContextType>({
  isConnected: false,
  unreadCount: 0,
  refreshUnreadCount: () => {},
})

export function useNotificationContext() {
  return useContext(NotificationContext)
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const queryClient = useQueryClient()
  const [identity, setIdentity] = useState('')
  // The provider outlives the login page. Reconnect when a new actor is loaded.
  useEffect(() => queryClient.getQueryCache().subscribe(event => {
    if (event.query.queryKey[0] !== 'current-user') return
    const user = event.query.state.data as {id?:string;scope?:string;roles?:string[]} | undefined
    setIdentity(event.type === 'removed' || !user ? '' : `${user.id}:${user.scope}:${user.roles?.join(',')}`)
  }), [queryClient])

  const refreshUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token')
      if (!token) return

      const response = await fetch('/api/v1/notifications?isRead=false&limit=1', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const jsonResponse = await response.json()
        setUnreadCount(jsonResponse.result?.total || 0)
      }
    } catch (error) {
      console.error('Failed to refresh unread count:', error)
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      return
    }

    // Initial unread count
    refreshUnreadCount()

    // badges 信号防抖：批量流转会短时多次广播，合并为一次重拉（300ms）
    let badgesTimer: ReturnType<typeof setTimeout> | undefined
    const bumpBadges = () => {
      if (badgesTimer) clearTimeout(badgesTimer)
      badgesTimer = setTimeout(() => {
        void refreshOrderTasks(queryClient)
      }, 300)
    }

    // Original mutations stay unchanged; successful operations share one refresh path.
    const unsubscribe = queryClient.getMutationCache().subscribe(event => {
      if (event.type === 'updated' && event.action.type === 'success') bumpBadges()
    })

    const onVisible = () => { if (document.visibilityState === 'visible') bumpBadges() }
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)

    // Create SSE client
    const sseClient = new SSEClient(
      (message: SSEMessage) => {
        if (message.type === 'connected') {
          setIsConnected(true)
        } else if (message.type === 'badges') {
          // 角标刷新信号（方案A）：待办变化 → 防抖后重拉 task-badges，实现实时角标
          bumpBadges()
        } else if (message.type === 'notification') {
          const notifMsg = message as NotificationMessage
          const notification = notifMsg.data

          // Show toast
          toast(notification.title, {
            description: notification.content,
            action: notification.related_order_id
              ? {
                  label: '查看',
                  onClick: () => {
                    useOrderDetailModal.getState().open(notification.related_order_id!)
                  },
                }
              : undefined,
          })

          // 未读数改为向后端拉权威值（取代乐观 +1，避免多端/断连漂移）
          refreshUnreadCount()

          // Invalidate notifications query
          queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
      },
      () => {
        // 错误处理：静默处理，避免噪音
        setIsConnected(false)
      },
      () => {
        // 连接成功/重连：拉权威未读数 + 重拉角标，补上断连期间遗漏的变化
        setIsConnected(true)
        refreshUnreadCount()
        bumpBadges()
      }
    )

    sseClient.connect()

    return () => {
      if (badgesTimer) clearTimeout(badgesTimer)
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
      unsubscribe()
      sseClient.disconnect()
      setIsConnected(false)
    }
  }, [queryClient, refreshUnreadCount, identity])

  return (
    <NotificationContext.Provider value={{ isConnected, unreadCount, refreshUnreadCount }}>
      {children}
    </NotificationContext.Provider>
  )
}
